import { AutofillBlock } from 'browser/components/atomic-elements/atoms/autofill-block/autofill-block'
import _ from 'lodash'
import React from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { BaseMap, IBaseMapProps } from './base-map'
import {
  getValidGeolocations,
  IMarkerClusterLayerProps,
  MarkerClusterLayer,
} from './layers/marker-cluster-layer'
import { DocumentMarker } from './markers/document-marker'
import { LocationMarker } from './markers/location-marker'
import { fitBounds } from './utils/fit-bounds'

// New York
const location = {
  latitude: 40,
  longitude: -100,
}

/**
 * @uiComponent
 */
interface IEntityMapProps extends IBaseProps {
  className?: string
  defaultLocation?: object
  entity?: object
  entities?: any[]
  geolocationPath?: string | string[]
  isInteractive?: boolean
  isMapExpanded?: boolean
  isZoomControlsEnabled?: boolean
  markersPaddingLeft?: number
  markerType?: string
  onSelect?: IMarkerClusterLayerProps['onSelect']
  padding?: object
  selectedEntity?: object
  handleToggleMapFullscreen?: () => void
  zoomLevel?: number
}

class InnerEntityMap extends React.Component<IEntityMapProps, any> {
  public static defaultProps: Partial<IEntityMapProps> = {
    defaultLocation: undefined,
    geolocationPath: 'document.address.geolocation',
    markerType: 'document',
    padding: { left: 5, right: 5, top: 5, bottom: 5 },
    zoomLevel: 2,
  }

  constructor(props: IEntityMapProps) {
    super(props)
    this.state = this.getNextStateFromProps(props)
    this.handleViewportChanged = this.handleViewportChanged.bind(this)
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IEntityMapProps) {
    if (nextProps.entity || this.props.entities !== nextProps.entities) {
      this.setState(this.getNextStateFromProps(nextProps, this.state))
    }
  }

  public getNextStateFromProps(
    props: IEntityMapProps,
    prevState?: { viewport?: IBaseMapProps['viewport'] }
  ) {
    const entities = this.getEntitiesWithLocation(props)
    const result = this.getBoundsFromEntities(entities, props)
    const viewport = _.get(prevState, 'viewport')
    const zoom = result ? result.zoom : props.zoomLevel!
    const maxFitPropsZoom = 15
    return {
      entities,
      viewport: {
        isDragging: _.get(viewport, 'isDragging'),
        latitude: result ? result.latitude : location.latitude,
        longitude: result ? result.longitude : location.longitude,
        startDragLngLat: _.get(viewport, 'startDragLngLat'),
        zoom: Math.min(zoom, maxFitPropsZoom),
      },
    }
  }

  public getEntitiesWithLocation(props: IEntityMapProps) {
    const { entity, entities, geolocationPath } = props
    let values: IEntityMapProps['entities'] = _.isEmpty(entities) && entity ? [entity] : entities
    values = _.filter(
      values,
      (entity) => !_.isEmpty(getValidGeolocations(entity, geolocationPath!))
    )
    return _.uniqBy(values, 'uniqueId')
  }

  public getBoundsFromEntities(entities: IEntityMapProps['entities'], props: IEntityMapProps) {
    const { geolocationPath, zoomLevel } = props
    const points = _.flatMap(entities, (entity) => getValidGeolocations(entity, geolocationPath!))
    if (!_.isEmpty(points)) {
      return {
        latitude: _.minBy(points, 'latitude')!.latitude,
        longitude: _.minBy(points, 'longitude')!.longitude,
        zoom: zoomLevel!,
      }
    }
  }

  public getMarkerComponent(): any {
    const { markerType } = this.props
    if (markerType === 'location') {
      return LocationMarker
    }
    return DocumentMarker
  }

  //////////////////////////////////////////////////////////////////////////////
  // Handlers
  //////////////////////////////////////////////////////////////////////////////

  public handleViewportChanged(opt: IBaseMapProps['viewport']) {
    this.setState({
      viewport: {
        isDragging: opt.isDragging,
        latitude: opt.latitude,
        longitude: opt.longitude,
        startDragLngLat: opt.startDragLngLat,
        zoom: opt.zoom,
      },
    })
  }

  public render() {
    const { entities, viewport } = this.state
    const MarkerComponent = this.getMarkerComponent()
    return (
      <BaseMap
        {...this.props as IBaseMapProps}
        onViewportChange={this.handleViewportChanged}
        viewport={viewport}
      >
        <MarkerClusterLayer
          {...this.props as IMarkerClusterLayerProps}
          entities={entities}
          MarkerComponent={MarkerComponent}
          markersHeight={MarkerComponent.height}
          markersWidth={MarkerComponent.width}
          onViewportChange={this.handleViewportChanged}
          viewport={viewport}
        />
      </BaseMap>
    )
  }
}

// TODO(peter): Why is it this way?
export class EntityMap extends React.PureComponent<IEntityMapProps, any> {
  public render() {
    return (
      <AutofillBlock>
        <InnerEntityMap {...this.props} />
      </AutofillBlock>
    )
  }
}
